import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { VideoCamera, Record, Stop, Upload, ArrowClockwise, Play, ClockCounterClockwise } from "@phosphor-icons/react";

const fmtAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

export default function VideoInterview() {
    const { applicationId } = useParams();
    const [recording, setRecording] = useState(false);
    const [blob, setBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [stream, setStream] = useState(null);
    const [history, setHistory] = useState([]);
    const videoRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/videos/mine");
            setHistory(data);
        } catch {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        return () => {
            if (stream) stream.getTracks().forEach((t) => t.stop());
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
        // eslint-disable-next-line
    }, []);

    const openPastInterview = async (item) => {
        try {
            const { data } = await api.get(`/videos/${item.file_id}`);
            setResult({
                path: data.storage_path,
                transcript: data.transcript || "",
                analysis: data.analysis || {},
                _historic: true,
                job_title: item.job_title,
                company_name: item.company_name,
                created_at: data.created_at,
            });
            // clear any local recording state — viewer mode only
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
            setBlob(null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load interview");
        }
    };

    const startCamera = async () => {
        try {
            const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            setStream(s);
            if (videoRef.current) videoRef.current.srcObject = s;
        } catch (e) {
            toast.error("Camera access denied");
        }
    };

    const startRecording = () => {
        if (!stream) return;
        chunksRef.current = [];
        const rec = new MediaRecorder(stream, { mimeType: "video/webm" });
        rec.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
            const b = new Blob(chunksRef.current, { type: "video/webm" });
            setBlob(b);
            setPreviewUrl(URL.createObjectURL(b));
        };
        rec.start();
        recorderRef.current = rec;
        setRecording(true);
    };

    const stopRecording = () => {
        recorderRef.current?.stop();
        setRecording(false);
    };

    const resetRecording = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setBlob(null);
        setPreviewUrl(null);
        setResult(null);
    };

    const uploadBlob = async (b, filename = "interview.webm") => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", b, filename);
            const url = applicationId
                ? `/upload/video?application_id=${applicationId}`
                : "/upload/video";
            const { data } = await api.post(url, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success("Uploaded. AI analysis complete.");
            loadHistory();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleFileUpload = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        uploadBlob(f, f.name);
    };

    return (
        <div data-testid="video-interview-page">
            <Navbar />
            <section className="max-w-[1100px] mx-auto px-6 py-8">
                <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-blue-600 mb-2">
                    Video interview · AI analysis
                </div>
                <h1 className="font-display text-4xl">Talk. We'll turn it into talent intelligence.</h1>
                <p className="text-slate-600 mt-2">
                    Record a 1–3 minute answer to: <span className="italic">"Walk me through a project you're proud of and the hardest trade-off you made."</span>
                </p>

                {history.length > 0 && (
                    <div className="mt-6 border border-slate-200" data-testid="video-history-list">
                        <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                            <div className="font-mono-plex text-xs uppercase tracking-[0.3em] text-slate-500 inline-flex items-center gap-1.5">
                                <ClockCounterClockwise size={12} /> Your past interviews
                            </div>
                            <span className="text-[10px] font-mono-plex text-slate-400">
                                {history.length} recorded
                            </span>
                        </div>
                        <div className="divide-y divide-slate-200 max-h-64 overflow-auto">
                            {history.map((h) => {
                                const a = h.analysis || {};
                                const tp = h.transcript_preview || "";
                                const transcriptFailed =
                                    !tp || tp.startsWith("[Transcription") || tp.startsWith("[Transcription failed");
                                const score = a.overall_score;
                                const hasScore = score !== undefined && score !== null;
                                return (
                                    <button
                                        key={h.file_id}
                                        type="button"
                                        onClick={() => openPastInterview(h)}
                                        data-testid={`video-history-row-${h.file_id}`}
                                        className="w-full text-left px-4 py-3 hover:bg-slate-50 grid grid-cols-[1fr_auto] gap-3 items-center"
                                    >
                                        <div className="min-w-0">
                                            <div className="font-mono-plex text-[10px] uppercase tracking-widest text-blue-600 truncate">
                                                {h.job_title
                                                    ? `${h.job_title} · ${h.company_name || ""}`
                                                    : "Practice recording"}
                                            </div>
                                            <div
                                                className={`text-sm truncate mt-0.5 ${
                                                    transcriptFailed ? "text-slate-400 italic" : "text-slate-700"
                                                }`}
                                                data-testid={`video-history-preview-${h.file_id}`}
                                            >
                                                {transcriptFailed
                                                    ? "Transcription unavailable for this clip"
                                                    : tp}
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-0.5">
                                                {fmtAgo(h.created_at)} · {h.filename || "interview"}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            {hasScore ? (
                                                <>
                                                    <div className="font-display text-2xl text-slate-950">
                                                        {score}
                                                        <span className="text-xs text-slate-400">/100</span>
                                                    </div>
                                                    <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-400">
                                                        {a.sentiment || "—"}
                                                    </div>
                                                </>
                                            ) : transcriptFailed ? (
                                                <span
                                                    data-testid={`video-history-status-${h.file_id}`}
                                                    className="text-[10px] font-mono-plex uppercase tracking-widest bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5"
                                                >
                                                    Failed
                                                </span>
                                            ) : (
                                                <span
                                                    data-testid={`video-history-status-${h.file_id}`}
                                                    className="text-[10px] font-mono-plex uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5"
                                                >
                                                    Pending
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="mt-8 grid md:grid-cols-[1.2fr_1fr] gap-6">
                    <div className="border border-slate-200 p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                                <VideoCamera size={14} /> Record
                            </div>
                            {previewUrl ? (
                                <button
                                    data-testid="video-reset-btn"
                                    onClick={resetRecording}
                                    className="text-xs px-2 py-1 border border-slate-300 hover:bg-slate-950 hover:text-white inline-flex items-center gap-1"
                                >
                                    <ArrowClockwise size={14} /> Retake
                                </button>
                            ) : null}
                        </div>

                        <div className="bg-slate-950 aspect-video relative flex items-center justify-center overflow-hidden">
                            {previewUrl ? (
                                <video data-testid="video-preview" src={previewUrl} controls className="w-full h-full" />
                            ) : (
                                <video data-testid="video-live" ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                            )}
                            {recording && (
                                <div className="absolute top-3 left-3 text-xs font-mono-plex uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 animate-pulse">
                                    ● REC
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                            {!stream && (
                                <button
                                    data-testid="video-start-camera-btn"
                                    onClick={startCamera}
                                    className="px-4 py-2 bg-slate-950 text-white hover:bg-blue-600 inline-flex items-center gap-1.5"
                                >
                                    <VideoCamera size={16} /> Enable camera
                                </button>
                            )}
                            {stream && !recording && !previewUrl && (
                                <button
                                    data-testid="video-record-btn"
                                    onClick={startRecording}
                                    className="px-4 py-2 bg-red-600 text-white hover:bg-slate-950 inline-flex items-center gap-1.5"
                                >
                                    <Record size={16} weight="fill" /> Start recording
                                </button>
                            )}
                            {recording && (
                                <button
                                    data-testid="video-stop-btn"
                                    onClick={stopRecording}
                                    className="px-4 py-2 bg-slate-950 text-white hover:bg-blue-600 inline-flex items-center gap-1.5"
                                >
                                    <Stop size={16} weight="fill" /> Stop
                                </button>
                            )}
                            {blob && !uploading && (
                                <button
                                    data-testid="video-upload-btn"
                                    onClick={() => uploadBlob(blob)}
                                    className="px-4 py-2 bg-blue-600 text-white hover:bg-slate-950 inline-flex items-center gap-1.5"
                                >
                                    <Upload size={16} /> Analyze with AI
                                </button>
                            )}
                            {uploading && (
                                <div className="text-sm font-mono-plex text-slate-500 self-center">
                                    Transcribing & analyzing…
                                </div>
                            )}
                        </div>

                        <div className="mt-4 border-t border-slate-200 pt-4">
                            <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-2">
                                Or upload a file
                            </div>
                            <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 cursor-pointer hover:bg-slate-950 hover:text-white text-sm">
                                <Upload size={16} />
                                Choose video/audio
                                <input
                                    data-testid="video-file-input"
                                    type="file"
                                    hidden
                                    accept="video/*,audio/*"
                                    onChange={handleFileUpload}
                                />
                            </label>
                            <p className="text-xs text-slate-500 mt-2">
                                Max 25 MB · mp3, mp4, m4a, wav, webm
                            </p>
                        </div>
                    </div>

                    <div className="border border-slate-200 p-5">
                        <div className="font-mono-plex text-xs uppercase tracking-widest text-blue-600 mb-2 inline-flex items-center gap-1.5">
                            <Play size={14} /> AI analysis
                        </div>
                        {!result && (
                            <div className="text-sm text-slate-500 mt-2">
                                Upload a recording to see transcript, sentiment, clarity and confidence scores.
                            </div>
                        )}
                        {result && (() => {
                            const a = result.analysis || {};
                            const transcript = result.transcript || "";
                            const failed = !transcript || transcript.startsWith("[Transcription");
                            const metrics = [
                                ["Overall", a.overall_score],
                                ["Communication", a.communication],
                                ["Clarity", a.clarity],
                                ["Confidence", a.confidence],
                                ["Engagement", a.engagement],
                                ["Warmth", a.warmth],
                                ["Composure", a.composure],
                            ].filter(([, v]) => v !== undefined && v !== null);
                            if (failed && metrics.length === 0) {
                                return (
                                    <div data-testid="analysis-result" className="text-sm text-slate-600">
                                        <div className="border border-slate-200 bg-slate-50 p-4 text-slate-500">
                                            Transcription failed for this clip — likely too short,
                                            silent, or an unsupported codec. Re-record a 30s+ answer
                                            with clear audio to get a full analysis.
                                        </div>
                                    </div>
                                );
                            }
                            return (
                            <div data-testid="analysis-result">
                                <div className="grid grid-cols-2 gap-2">
                                    {metrics.map(([k, v]) => (
                                        <div key={k} className="border border-slate-200 p-3 text-center">
                                            <div className="font-display text-2xl">{v ?? "—"}</div>
                                            <div className="text-[10px] font-mono-plex uppercase tracking-widest text-slate-500">{k}</div>
                                        </div>
                                    ))}
                                </div>
                                {a.emotional_cues?.length > 0 && (
                                    <div className="mt-4">
                                        <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-1">
                                            Emotional cues
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {a.emotional_cues.map((c) => (
                                                <span key={c} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 font-mono-plex">
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="mt-4 text-sm">
                                    <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-1">
                                        Sentiment
                                    </div>
                                    <div className="uppercase font-mono-plex">{a.sentiment || "—"}</div>
                                </div>
                                <div className="mt-4 text-sm">
                                    <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500 mb-1">
                                        Summary
                                    </div>
                                    <p>{a.summary || "—"}</p>
                                </div>
                                {!failed && (
                                    <details className="mt-4 text-xs text-slate-600">
                                        <summary className="cursor-pointer font-mono-plex uppercase tracking-widest">
                                            Transcript
                                        </summary>
                                        <p className="mt-2 whitespace-pre-wrap">{transcript}</p>
                                    </details>
                                )}
                            </div>
                            );
                        })()}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
